import React from 'react';
import { useQuiz } from '../context/QuizContext';
import { typeShortCode } from '../mock';
import { t } from '../i18n';
import { X } from 'lucide-react';

const Label = ({ children }) => <label className="mg-label">{children}</label>;

const QuestionCard = ({ q, index }) => {
  const { updateQuestion, removeQuestion, lang } = useQuiz();
  const set = (patch) => updateQuestion(q.id, patch);

  const setOption = (i, val) => {
    const options = [...(q.options || [])];
    options[i] = val;
    set({ options });
  };
  const setPair = (i, k, val) => {
    const pairs = [...(q.pairs || [])];
    pairs[i] = { ...pairs[i], [k]: val };
    set({ pairs });
  };
  const addPair = () => set({ pairs: [...(q.pairs || []), { q:'', a:'' }] });
  const removePair = (i) => set({ pairs: q.pairs.filter((_, idx) => idx !== i) });

  const setAnswer = (i, val) => {
    const answers = [...(q.answers || [])];
    answers[i] = val;
    set({ answers });
  };
  const addAnswer = () => set({ answers: [...(q.answers || []), ''] });

  const optionCount = (q.options || []).length;
  const addOption = () => set({ options: [...(q.options || []), ''] });
  const removeOption = (i) => {
    const options = q.options.filter((_, idx) => idx !== i);
    let patch = { options };
    if (q.type === 'mc-single' && q.correct === i) patch.correct = 0;
    if (q.type === 'mc-multiple') patch.corrects = (q.corrects || []).filter(c => c !== i).map(c => c > i ? c-1 : c);
    set(patch);
  };

  return (
    <div className="mg-panel">
      <button className="mg-close-x" onClick={() => removeQuestion(q.id)} title={t('delete', lang)}>
        <X size={16} />
      </button>

      <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom: 12 }}>
        <span className="mg-badge">#{index + 1} · {typeShortCode(q.type)}</span>
      </div>

      <Label>{t('questionTitle', lang)}</Label>
      <input className="mg-input" value={q.title} onChange={e => set({ title: e.target.value })} />

      <div style={{ height: 10 }} />
      <Label>{t('question', lang)}</Label>
      <textarea className="mg-textarea" placeholder={t('questionPh', lang)} value={q.question} onChange={e => set({ question: e.target.value })} />

      {(q.type === 'mc-single' || q.type === 'mc-multiple') && (
        <div style={{ marginTop: 12 }}>
          {q.options.map((opt, i) => (
            <div className="mg-row" key={i}>
              {q.type === 'mc-single' ? (
                <input type="radio" name={`correct-${q.id}`} checked={q.correct === i} onChange={() => set({ correct: i })} />
              ) : (
                <input type="checkbox" checked={(q.corrects || []).includes(i)}
                  onChange={(e) => {
                    const set2 = new Set(q.corrects || []);
                    if (e.target.checked) set2.add(i); else set2.delete(i);
                    set({ corrects: Array.from(set2).sort((a,b)=>a-b) });
                  }} />
              )}
              <span className="mg-opt-label">{t('option', lang)} {i + 1}:</span>
              <input className="mg-input" placeholder={t('optionPh', lang)} value={opt} onChange={e => setOption(i, e.target.value)} />
              {optionCount > 2 && (
                <button className="mg-btn danger" style={{ width: 40, padding: '6px' }} onClick={() => removeOption(i)} title={t('delete', lang)}>×</button>
              )}
            </div>
          ))}
          <div style={{ marginTop: 8 }}>
            <button className="mg-btn ghost" style={{ width: 'auto', padding: '6px 12px' }} onClick={addOption}>+ {t('option', lang)}</button>
          </div>
        </div>
      )}

      {q.type === 'truefalse' && (
        <div style={{ marginTop: 12 }}>
          <Label>{t('correctAnswer', lang)}</Label>
          <select className="mg-select" value={q.correct} onChange={e => set({ correct: e.target.value })}>
            <option value="true">{t('trueText', lang)}</option>
            <option value="false">{t('falseText', lang)}</option>
          </select>
        </div>
      )}

      {q.type === 'short' && (
        <div style={{ marginTop: 12 }}>
          {q.answers.map((a, i) => (
            <div className="mg-row" key={i}>
              <span className="mg-opt-label">{t('answer', lang)} {i + 1}:</span>
              <input className="mg-input" placeholder={t('answerPh', lang)} value={a} onChange={e => setAnswer(i, e.target.value)} />
              {q.answers.length > 1 && (
                <button className="mg-btn danger" style={{ width: 40, padding: '6px' }} onClick={() => set({ answers: q.answers.filter((_, idx) => idx !== i) })}>×</button>
              )}
            </div>
          ))}
          <button className="mg-btn ghost" style={{ width: 'auto', padding: '6px 12px', marginTop: 6 }} onClick={addAnswer}>{t('addAnswer', lang)}</button>
        </div>
      )}

      {q.type === 'numerical' && (
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <Label>{t('numAnswer', lang)}</Label>
            <input className="mg-input" type="number" value={q.answer} onChange={e => set({ answer: e.target.value })} />
          </div>
          <div>
            <Label>{t('tolerance', lang)}</Label>
            <input className="mg-input" type="number" value={q.tolerance} onChange={e => set({ tolerance: e.target.value })} />
          </div>
        </div>
      )}

      {q.type === 'matching' && (
        <div style={{ marginTop: 12 }}>
          {q.pairs.map((p, i) => (
            <div className="mg-row" key={i}>
              <input className="mg-input" placeholder={t('matchQ', lang) + ' ' + (i+1)} value={p.q} onChange={e => setPair(i, 'q', e.target.value)} />
              <input className="mg-input" placeholder={t('matchA', lang) + ' ' + (i+1)} value={p.a} onChange={e => setPair(i, 'a', e.target.value)} />
              {q.pairs.length > 1 && (
                <button className="mg-btn danger" style={{ width: 40, padding: '6px' }} onClick={() => removePair(i)}>×</button>
              )}
            </div>
          ))}
          <button className="mg-btn ghost" style={{ width: 'auto', padding: '6px 12px', marginTop: 6 }} onClick={addPair}>{t('addPair', lang)}</button>
        </div>
      )}

      {q.type === 'essay' && (
        <div style={{ marginTop: 12 }}>
          <Label>{t('sampleAnswer', lang)}</Label>
          <textarea className="mg-textarea" value={q.sampleAnswer} onChange={e => set({ sampleAnswer: e.target.value })} />
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
